<script lang="ts">
  import { onMount, untrack } from 'svelte';
  /* THE MAP, AND NOTHING ELSE.
   *
   * ═══ WHAT LEFT ═══
   *
   * A floating layer strip centred on the window, with two media queries
   * recentring it between the panel and the tool column;
   * a `Scrubber` at `bottom: 34px`; a `ChartTools` column at `right: 8px; top:
   * 154px` (190 on a coarse pointer); a `.routebox` at `right: 12px; bottom:
   * 96px` capped at `calc(100dvh - 190px)`; a `MobileSheet` for the route; and
   * an object `Sheet` at `top: 140px` with a `bind:this` so this file could call
   * `onTap` on it.
   *
   * Every one of those was chrome, and chrome is the shell's business now. What
   * is left is what genuinely needs a MapLibre instance: which packs to build,
   * where the chart opens, and what a finger on the canvas means.
   *
   * ═══ AND THE THREE REDRAW PATHS STAYED ═══
   *
   * The 1 Hz sweep is right for live data and wrong for anything a finger is
   * doing. The two effects below redraw the layers a gesture actually changed,
   * at the rate the gesture changes them, and both are `untrack`ed for reasons
   * that were measured rather than guessed.
   */
  import Chart from '@gennetay/chart-ui/Chart';
  import { measure } from '@gennetay/chart-core/lib/measure.svelte';
  import { threeD } from '@gennetay/chart-core/lib/three-d.svelte';
  import { theme } from '@gennetay/chart-core/lib/theme.svelte';
  import { autoroute } from '@gennetay/chart-core/lib/autoroute.svelte';
  import { routePlan } from '@gennetay/chart-core/lib/routeplan.svelte';
  import { currents } from '@gennetay/chart-layers/packs/tides/currents.svelte';
  import { seaCurrents } from '@gennetay/chart-layers/packs/tides/seacurrents.svelte';
  import { gatesFromStations } from '@gennetay/chart-core/lib/currents';
  import { editor, nearestLeg, nearestVertex } from '@gennetay/chart-core/lib/editor.svelte';
  import { viewTime } from '@gennetay/chart-core/lib/viewtime.svelte';
  import { shouldRecalcOnDrag } from '@gennetay/chart-core/lib/routesummary';
  import type { ChartEnv } from '@gennetay/chart-core/map/env';
  import type { Map as MlMap } from 'maplibre-gl';
  import { openingView, rememberView } from './openingview';
  import { panels } from '@gennetay/chart-ui/shell';
  import { picked } from './pick.svelte';
  import { setMap } from './camera.svelte';
  import { tileProgress } from '@gennetay/chart-core/map/tile-progress.svelte';
  import {
    backstopCase,
    CHART_LOADING_GRACE_MS,
    dismissPillWhenTilesSettle,
    loadingPillText,
    type ChartBackstopKind
  } from './chart-loading';
  import 'maplibre-gl/dist/maplibre-gl.css';

  /* ═══ WHICH PACKS A HOST WITH NO BOAT ASKS FOR ═══
   *
   * Everything whose data ships with the build or comes off a public service.
   * `boat` and `traffic` are absent entirely — there is no vessel, no AIS feed
   * and no SignalK to write a route to, and the catalogue does not offer them
   * for exactly that reason.
   *
   * `poi` is ActiveCaptain, and it is here rather than in `traffic` — where it
   * used to be, and where being grouped with AIS kept it off this chart
   * entirely — because it needs a list of places and no vessel at all. */
  const PACKS = ['base', 'tides', 'weather', 'fishing', 'tools', 'poi'];

  let {
    onmap,
    /** Bumped on every `moveend`. MapLibre's bounds are not reactive state and
     *  nothing else would tell the tideline the chart had been panned. */
    onmove,
    /** The 1 Hz sweep, handed up. Five components used to own a `setInterval`
     *  each; the chart already ticks, so they read one number instead. */
    ontick
  }: { onmap?: (m: MlMap) => void; onmove?: () => void; ontick?: () => void } = $props();

  /* ═══ WHERE IT OPENS ═══
   * Read ONCE, not derived. The opening view is a decision taken at startup;
   * making it reactive would mean the map jumping back to it the moment the
   * remembered value is rewritten, which is every time you stop panning. */
  const opening = openingView();

  let tick: ((env: ChartEnv) => void) | null = null;
  let map = $state<MlMap | null>(null);
  let chart = $state<ReturnType<typeof Chart> | null>(null);
  let chartsState = $state<'loading' | 'ready'>('loading');
  let chartsTimedOut = $state(false);
  let timedOutKind = $state<ChartBackstopKind>(null);
  // The host pill is the post-ready loading voice (counts, backstop, Retry).
  // The shared Chart's quiet fault owns the pre-ready window, so the pill
  // stays unmounted until the map is ready — one loading voice at a time (#148).
  let mapReady = $state(false);

  // The 60s-after-mount backstop covers both ways loading can fail visibly:
  // no tile requests means the pipeline never started (#28), while requested
  // tiles that never finish mean it stalled (#144). A chart that reached ready
  // before the deadline is never timed out, including a successful load that
  // did not use our tile protocol. Retry arms a fresh 60s window.
  let backstopTimer: ReturnType<typeof setTimeout> | null = null;
  function armBackstop() {
    if (backstopTimer) clearTimeout(backstopTimer);
    timedOutKind = null;
    backstopTimer = setTimeout(() => {
      const kind = backstopCase(tileProgress.requested, chartsState);
      if (kind) {
        timedOutKind = kind;
        chartsTimedOut = true;
      }
    }, CHART_LOADING_GRACE_MS);
  }

  onMount(() => {
    armBackstop();
    return () => {
      if (backstopTimer) clearTimeout(backstopTimer);
    };
  });

  $effect(() => {
    if (!chartsTimedOut) return;
    if (chartsState === 'ready') {
      // The load finished after the deadline — the note's job is done.
      chartsTimedOut = false;
      timedOutKind = null;
    } else if (timedOutKind === 'zero' && tileProgress.requested > 0) {
      // The pipeline woke up after the zero-request timeout.
      chartsTimedOut = false;
      timedOutKind = null;
    }
  });

  /* ═══ THE STORES, PLUGGED INTO THE HEADLESS ROUTER ═══
   *
   * chart-core cannot import chart-layers, so the passage router is handed its
   * data rather than reaching for it — the same seam `configureSources` uses.
   *
   * `currents.load()` is fired because the gates need the stations and a
   * planning feature must not depend on the user having found the tidal-stream
   * layer toggle first. */
  $effect(() => {
    void currents.load();
    autoroute.configurePassage({
      gates: (boatKn) => gatesFromStations(currents.stations, boatKn),
      field: () => seaCurrents.window(),
      sampler: () => seaCurrents.passageSampler()
    });
    return () => autoroute.configurePassage(null);
  });

  /* SCRUBBING. Every prediction layer reads the cursor, so a scrub repaints
   * them all — but only when the cursor MOVES, which is nothing at all while it
   * is live and the sweep is already handling it. */
  $effect(() => {
    void viewTime.now;
    const live = viewTime.isLive;
    /* UNTRACKED, so the cursor is the ONLY trigger. `refresh` walks every module
     * and every one of them reads $state on its way through — without this the
     * effect subscribes to all of it and re-runs on anything any layer touches,
     * which is a redraw storm rather than a redraw. */
    untrack(() => {
      if (!live) chart?.refresh();
    });
  });

  /* THE ROUTE BEING DRAGGED. One layer, on the edit — not the whole loop at
   * pointer rate, which would be sixty passes a second over twenty modules.
   *
   * `autoroute.preview` is the SECOND trigger and it is not optional: while the
   * finger is down the route is untouched and only the router's answer moves,
   * so `editor.rev` alone leaves the dashed proposal frozen where the drag
   * started. */
  $effect(() => {
    void editor.rev;
    void autoroute.preview;
    /* UNTRACKED for the same reason, and the boat measured the cost of not
     * doing it: `update` reads `pts` and `selected`, which are $state too, so
     * the effect subscribes to them as well and runs TWICE per move. */
    untrack(() => chart?.refresh(['route-edit']));
  });

  /** How far from a vertex counts as grabbing it. The editor's own 14 px — a
   *  wet finger on a moving deck, and the same target the boat uses. */
  const HIT_PX = 14;
  const ptsPx = (m: MlMap) => editor.pts.map((p) => m.project([p.lon, p.lat]));
  let dragIndex: number | null = null;

  /* Imported here rather than at the top so the catalogue and every pack it
   * reaches stay off the critical path — the same reason MapLibre is lazy. */
  async function build(env: ChartEnv) {
    const { buildPacks } = await import('@gennetay/chart-layers/catalogue');
    const built = await buildPacks(PACKS, env);
    tick = built.tick;
    if (import.meta.env.DEV) {
      /* The current-field store, for the proofs. How many model HOURS it holds
       * distinguishes a decoded binary window from a layer that merely exists
       * on the map, and nothing in the DOM says it. */
      const { seaCurrents } = await import('@gennetay/chart-layers/packs/tides/seacurrents.svelte');
      (globalThis as Record<string, unknown>).__seaCurrents = seaCurrents;
      (globalThis as Record<string, unknown>).__editor = editor;
      /* ═══ THE TIME CURSOR, AS A TEST SEAM ═══
       *
       * The proofs used to scrub by dispatching an `input` event at the
       * scrubber's `<input type=range>`. There is no range input any more —
       * the tideline is a strip of hour cells precisely because a slider could
       * not place a finger inside a one-hour slack window — so a proof that
       * pokes at one is testing a control that was removed for being unusable.
       *
       * Exposing the store is the honest seam: a proof that wants the chart at
       * 14:20 should say so, not simulate a gesture at a widget. DEV only,
       * alongside the two that were already here. */
      const { viewTime: vt } = await import('@gennetay/chart-core/lib/viewtime.svelte');
      (globalThis as Record<string, unknown>).__viewTime = vt;
      /* ═══ AND THE ROUTER, SO A PROOF CAN PIN A DEPARTURE ═══
       *
       * `_passage-proof` asserts that the passage HOLDS for slack at Porlier,
       * and whether it does depends on what time the proof is run: leave at
       * 09:16 and the boat arrives outside the window and waits; leave at 08:57
       * and it arrives inside one and sails straight through. Both are correct
       * behaviour and only one of them is the thing being tested.
       *
       * A proof whose result depends on the wall clock cries wolf, and this
       * repo has been trained to ignore proofs that do. Setting the departure
       * makes the question deterministic. */
      (globalThis as Record<string, unknown>).__autoroute = autoroute;
    }
    return built.modules;
  }
</script>

<Chart
  bind:this={chart}
  {build}
  tick={(env) => {
    tick?.(env);
    ontick?.();
  }}
  center={opening.center}
  zoom={opening.zoom}
  assetBase={import.meta.env.BASE_URL}
  expose={import.meta.env.DEV}
  night={theme.night}
  threeD={threeD.on}
  exaggeration={threeD.exaggeration}
  loadingTimedOut={chartsTimedOut}
  loadingRequested={tileProgress.requested}
  onloadingretry={() => {
    chartsTimedOut = false;
    chartsState = 'loading';
    armBackstop();
  }}
  onready={(m) => {
    map = m;
    mapReady = true;
    rememberView(m);
    setMap(m);
    onmap?.(m);
    m.on('moveend', () => onmove?.());
    dismissPillWhenTilesSettle(m, tileProgress.requested, () => {
      chartsState = 'ready';
    });
  }}
  ondragstart={(_at, point) => {
    /* CLAIMED ONLY WHEN THERE IS A VERTEX UNDER THE PIXEL. Anything else and
     * the map pans, which is what a chart must keep doing by default. */
    if (editor.mode === 'off' || !map) return false;
    const i = nearestVertex(ptsPx(map), point, HIT_PX);
    if (i === null) return false;
    dragIndex = i;
    editor.selected = i;
    editor.beginMove(i);
    return true;
  }}
  ondragmove={(at) => {
    if (dragIndex === null) return;
    editor.moveTo(dragIndex, at);
    /* ═══ AND RE-SOLVE, LIVE, WHILE THE FINGER IS DOWN ═══
     *
     * On an autorouted draft every intermediate point is the ROUTER'S own work,
     * not a decision anybody made — so dragging one says "go through here", not
     * "freeze the router's guess". Showing the answer only on release would
     * mean dragging a line that then jumps somewhere else.
     *
     * Cheap to call per move: `routeLive` keeps ONE solve in flight and always
     * asks the newest question, so the cadence is the solve time itself. */
    if (!shouldRecalcOnDrag(true, dragIndex, editor.pts.length)) return;
    const pts = editor.pts;
    const last = pts.length - 1;
    /* The points a PERSON chose: the two ends, plus whatever is being dragged.
     * Everything between is the router's and is re-solved. */
    const idx = dragIndex === 0 || dragIndex === last ? [0, last] : [0, dragIndex, last];
    autoroute.routeLive(
      idx.map((k) => ({ lat: pts[k]!.lat, lon: pts[k]!.lon })),
      routePlan.draftM
    );
  }}
  ondragend={() => {
    dragIndex = null;
    /* COMMIT THE PREVIEW rather than re-solving from scratch: it is the same
     * question, already answered, and re-asking makes the route visibly jump on
     * release when the two answers differ. */
    const preview = autoroute.preview;
    if (preview && preview.length >= 2) editor.recalc(preview.map((p) => ({ ...p })));
    autoroute.clearPreview();
  }}
  ontap={(at, point) => {
    /* ═══ AN ARMED TOOL TAKES THE TAP BEFORE ANYTHING ELSE SEES IT ═══
       Otherwise a ruler point dropped on top of a tide station would set the
       point AND open the station, which is one gesture doing two things. */
    if (measure.active) {
      measure.tap(at);
      return;
    }
    if (routePlan.armed) {
      void routePlan.tap(at);
      return;
    }
    /* ═══ EDITING THE SOLVED ROUTE ═══
     *
     * A tap on a vertex SELECTS it; a tap on a leg INSERTS a point there. Both
     * are only offered while a route is being edited — with none, a tap falls
     * through to the pick, which is what an untouched chart should do. */
    if (editor.mode !== 'off' && map) {
      const pp = ptsPx(map);
      const vi = nearestVertex(pp, point, HIT_PX);
      if (vi !== null) {
        editor.selected = editor.selected === vi ? null : vi;
        return;
      }
      const leg = nearestLeg(pp, point);
      if (leg && leg.dist <= HIT_PX) {
        editor.insert(leg.index, at);
        return;
      }
    }
    /* A tap does not ACT — it ASKS. See pick.svelte.ts.
     *
     * The panel is opened HERE, once, by the gesture that produced the hits —
     * not by an effect watching the hit list. An effect is a standing rule, and
     * a standing rule that says "the pick should be open" re-opens it every
     * time the user asks for anything else. */
    const m = map;
    if (m && picked.tap(point, (ll) => m.project(ll))) panels.show('pick', 'open');
  }}
/>

{#if mapReady && chartsState === 'loading' && !chartsTimedOut}
  <div class="chart-status" role="status">
    {loadingPillText(tileProgress.requested, tileProgress.settled)}
  </div>
{/if}

<style>
  .chart-status {
    position: absolute;
    z-index: 4;
    top: var(--sp-3);
    left: 50%;
    transform: translateX(-50%);
    max-width: calc(100% - 2 * var(--sp-5));
    padding: var(--sp-2) var(--sp-4);
    border: 1px solid var(--line);
    border-radius: 999px;
    background: var(--s-1);
    color: var(--ink);
    font: var(--fs-read) / var(--lh-read) var(--sans);
    text-align: center;
    pointer-events: none;
  }
</style>
